import Vue from 'vue';
import { mapGetters } from 'vuex';
import path from 'path';
import _ from 'lodash';
import { cssGenerator } from '@/mixins/css-generator';
import tooltip from '@/mixins/components/tooltip';
import { ASSETS, SQUASH_DROPLET } from '@/constants';
import DropletOptions from '../_bubble-options';
import VideoPlayerOptions from './components/_edit-options';
import schema from './droplet.schema';
const dropletSchema = schema();

export default Vue.component('video-player', {
  template: require('./template.html'),
  mixins: [cssGenerator],
  computed: {
    ...mapGetters([
      'currentPage',
      'currentSite',
      'viewport',
      'localStyle',
      'activeDroplet',
      'activeState',
      'animationPreview',
      'dropletObserver',
      'dragging',
      'globalOpenModal',
      'globalBubbleOptionsOpen',
    ]),
    profile() {
      return this._dropletType(this.droplet);
    },
    video() {
      const thumbnail = this.droplet.thumbnail;
      const mainThumb = `${this._servePrefix(thumbnail || this.backupThumb)}${thumbnail || this.backupThumb}`;
      const mainSrc = this._servePrefix(this.droplet.videoFile) + this.droplet.videoFile;
      const linkedThumb = this._servePrefix(this.mediaItem.thumbnail) + this.mediaItem.thumbnail;
      const src = this.mediaItem.media || '';
      const linkedSrc = this._servePrefix(src) + src;
      const linked = this.droplet.ref || this.droplet.latestDynamic;
      return {
        src: linked ? linkedSrc : mainSrc,
        thumbnail: linked ? linkedThumb : mainThumb,
      };
    },
    mediaItem() {
      const enabled = this.currentSite.dynamic && this.currentSite.dynamic.media;
      const items = enabled ? (enabled.published || []) : [];
      const ref = this.droplet.ref;
      const latest = this.droplet.latestDynamic;
      let item;
      if (ref && enabled && !latest) {
        item = _.find(items, p => p._id === ref);
      }
      if (enabled && latest) {
        item = items.reduce(
          (a, b) => (
            new Date(a.released.date || a.publishedOn) >
            new Date(b.released.date || b.publishedOn) ? a : b
          ),
        );
      }
      return item ||
      {
        title: 'No Media Linked',
        thumbnail: `${process.env.S3_ASSETS_URL}mock-images/default-images/mock-media.svg`,
        media: `${process.env.S3_ASSETS_URL}multi-media/sample-audio.mp3`,
        externalMedia: {},
        summary: 'No summary provided.',
        categories: [],
        groups: [],
        created: new Date().toISOString(),
        _placeholder: true,
      };
    },
    mediaType() {
      const file = path.extname(this.mediaItem.media);
      const audio = ['.mp3', '.wmv', '.wav'];
      const video = ['.mp4', '.mov', '.mpeg', '.mpg'];
      let type;
      if (audio.includes(file)) {
        type = 'audio';
      } else if (video.includes(file)) {
        type = 'video';
      }
      return type;
    },
    checks() {
      const item = this.mediaItem;
      const isAnyVideo = (item.media && this.mediaType === 'video') || (!item.media && item.externalMedia.youtube);
      const isUploadedAudio = (item.media && this.mediaType === 'audio') || (!item.media && !item.externalMedia.youtube);
      const isUploadedVideo = item.media && this.mediaType === 'video';
      const isYouTubeFallback = !item.media && item.externalMedia.youtube;
      const noDisplay = !item.media && !item.externalMedia.youtube;
      return {
        isAnyVideo,
        isUploadedAudio,
        isUploadedVideo,
        isYouTubeFallback,
        noDisplay,
      };
    },
    hideTransform() {
      return this.dragging || this.globalBubbleOptionsOpen;
    },
    roamingTemplate() {
      return this.currentPage.type !== 'template' && this.droplet.templateRegion;
    },
    isActiveDroplet() {
      return this.activeDroplet._id === this.droplet._id;
    },
    displayStatus() {
      let settings = {
        desktop: 'default',
        tablet: 'default',
        mobile: 'default',
      };
      const selector = _.find(this.droplet.mediaQueries, { selector: ', .drzVideo' });
      if (selector) {
        const property = _.find(selector.properties, { property: 'display' });
        if (property) {
          settings = property;
        }
      }
      return settings;
    },
  },
  data() {
    return {
      type: dropletSchema.type,
      SQUASH_DROPLET,
      display: {
        top: null,
        left: null,
        right: null,
        bottom: null,
        ctPaddingTop: null,
        ctPaddingBottom: null,
        ctPaddingLeft: null,
        ctPaddingRight: null,
        transform: null,
        width: null,
        perspective: null,
        'box-shadow': null,
        'border-left-width': null,
        'border-right-width': null,
        'border-top-width': null,
        'border-bottom-width': null,
        'border-top-left-radius': null,
        'border-top-right-radius': null,
        'border-bottom-left-radius': null,
        'border-bottom-right-radius': null,
        'padding-top': null,
        'padding-bottom': null,
        'padding-right': null,
        'padding-left': null,
      },
      backupThumb: `${ASSETS}mock-images/default-images/video-droplet-thumb.svg`,
      hidden: false,
      dropletHovered: false,
      setBubbleSize: 'dropletBubble-normal',
      showEditBubble: false,
      showRevealClass: false,
    };
  },
  name: 'video-player',
  props: ['droplet', 'items', 'index'],
  components: {
    DropletOptions,
    VideoPlayerOptions,
    tooltip,
  },
  watch: {
    isActiveDroplet(is) {
      if (is) {
        this._scrollToDroplet(this.$el);
      }
    },
    'activeDroplet._id': function c(id) {
      if (!id) {
        this.showRevealClass = false;
      }
    },
    activeState(state) {
      if (state === ':reveal' && this.isActiveDroplet) {
        this.showRevealClass = true;
      } else {
        this.showRevealClass = false;
      }
    },
    animationPreview(should) {
      if (should && this.isActiveDroplet) {
        const droplet = this.$refs.dropletRow;
        const $droplet = $(droplet);
        $droplet.drzOnReveal({
          editing: this.isActiveDroplet,
          child: '.drzVideo',
        });
      }
    },
    viewport() {
      this.callCssGenerator();
      this.observeVisibility();
    },
    'localStyle.value': function setLocals() {
      this.setDisplay();
    },
    globalOpenModal(open) {
      if (!open) {
        this.unBindPlugin();
        this.bindPlugin();
      }
    },
    dropletObserver(id) {
      if (this.droplet._id === id) {
        this.observeVisibility();
        this.$store.dispatch('setDropletObserver', false);
      }
    },
  },
  mounted() {
    this.callCssGenerator();
    $(document).ready(this.bindPlugin);
    this.observeVisibility();
  },
  methods: {
    callCssGenerator() {
      return this.generateCSS({
        drp: this.droplet,
      });
    },
    observeVisibility() {
      const display = this.displayStatus;
      if (display[this.viewport] === 'none' || (display[this.viewport] === 'default' && display.desktop === 'none')) {
        this.hidden = true;
      } else {
        this.hidden = false;
      }
    },
    setDisplay() {
      if (this.activeDroplet._id === this.droplet._id) {
        const styles = this.localStyle;
        if (styles.properties.length > 0) {
          _.forEach(styles.properties, (property) => {
            this.$set(this.display, property, styles.value);
          });
        } else {
          _.forOwn(this.display, (value, key) => {
            this.display[key] = null;
          });
        }
      }
    },
    bindPlugin() {
      const $player = $(this.$refs.drzVideo);
      $player.drzVideoPlayer({
        thumbnail: this.droplet.enableThumb,
        hideControls: this.droplet.hideControls,
        link: this.droplet.hasLink ? '#' : false,
        newWindow: this.droplet.newWindow,
      });
    },
    unBindPlugin() {
      const ref = this.$refs.drzVideo;
      const $player = $(ref);
      if (ref) {
        $player.drzVideoPlayer.destroy($player);
      }
    },
    toggleBubble() {
      if (!this.roamingTemplate) {
        this.showEditBubble = !this.showEditBubble;
      }
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/video-player/index.js